import React, { useState } from "react";
import { ArrowUpRight, X } from "lucide-react";
import config from "../config";
import { canOpenNativeApp, isIosSafari } from "../constants/appStore";
import "./OpenInApp.css";

export function getAppUrl(target: "t" | "b", shareId: string): string {
  return `${config.deepLinkScheme}://${target}/${encodeURIComponent(shareId)}`;
}

// A direct user gesture requests the app. Browser focus/timing cannot tell us
// whether it is installed. Never schedule an automatic store redirect.
// Local/dev links still target the dev binary, never the App Store binary.
export function openInApp(target: "t" | "b", shareId: string): void {
  if (canOpenNativeApp() && shareId) window.location.assign(getAppUrl(target, shareId));
}

const DISMISSED_KEY = "vs-open-in-app-dismissed";
function readDismissed(): boolean {
  try { return window.sessionStorage.getItem(DISMISSED_KEY) === "1"; } catch { return false; }
}
function writeDismissed(): void {
  try { window.sessionStorage.setItem(DISMISSED_KEY, "1"); } catch { /* private mode: dismiss for this render only */ }
}

// Whether this page shows its own Open in app row. Safari on iOS draws
// Apple's "Open in the Vibesplit app" banner for /b/* exactly when the app is
// installed, and a web page has no other way to know that, so Safari gets
// nothing of ours. The other iOS browsers and in-app web views, where iOS
// draws nothing, get one quiet row. Android and desktop have no app.
export function showsOpenInAppRow(): boolean {
  return canOpenNativeApp() && !isIosSafari();
}

// One quiet row above a public bill/trip (#53 follow-up): `Open in app` with
// an arrow, the whole row the button, and a dismiss for the tab. A text link,
// not a pill: the header's Share bill pill is the one pill at the top. No
// logo mark, no question, no install variant — the settled confirmation
// carries the one download invite (#43). Deep link only.
export function OpenInAppRow({ target, shareId }: { target: "t" | "b"; shareId: string }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  if (!showsOpenInAppRow() || !shareId || dismissed) return null;
  return (
    <div className="vs-design open-in-app-row" role="region" aria-label="Open in app">
      <button type="button" className="open-in-app-row__open" onClick={() => openInApp(target, shareId)}>
        Open in app<ArrowUpRight size={16} aria-hidden="true" />
      </button>
      <button type="button" className="open-in-app-row__dismiss" aria-label="Dismiss" onClick={() => { writeDismissed(); setDismissed(true); }}>
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}
